<script setup lang="ts">
import type { PositionColumn, PositionRow, PredefinedPosition } from '@/components/finest-custom-position-list/types'
import FinestCustomPositionListCard from '@/components/FinestCustomPositionListCard.vue'
import { idOf, usePersistedRows } from '@/composables/usePersistedRows'
import { blankIncomeRow, incomePayload, isPositionDraft } from '@/pages/investors/credit/creditPositions'
import { incomeSuggestions } from '@/pages/investors/credit/incomeSuggestions'
import type { IncomeGroupKey } from '@/pages/investors/credit/types'
import { requestJson } from '@/utils/postJson'
import IncomePositionController from '@controllers/Investors/IncomePositionController'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import IncomePositionData = App.Data.SelfDisclosure.IncomePositionData

const props = defineProps<{
  group: IncomeGroupKey
  years: number[]
  personId: string
  serverRows: PositionRow[]
}>()

const { t } = useI18n({ useScope: 'local' })

const positionRoute = (id: string) => ({ naturalPerson: props.personId, incomePosition: id })

const { rows, reset, addDraft, removeRow, persistOnLeave } = usePersistedRows({
  toPayload: (row: PositionRow) => incomePayload(row, props.group, props.years),
  isDraft: isPositionDraft,
  create: async (payload) =>
    idOf(await requestJson<IncomePositionData>('post', IncomePositionController.store(props.personId).url, payload)),
  update: (id, payload) => requestJson('put', IncomePositionController.update(positionRoute(id)).url, payload),
  destroy: (id) => requestJson('delete', IncomePositionController.destroy(positionRoute(id)).url, undefined),
  reloadProps: ['people', 'creditCompletion'],
})

reset(props.serverRows)

watch(() => props.serverRows, reset)

const suggestions = computed<PredefinedPosition[]>(() =>
  incomeSuggestions[props.group].map((name) => ({ key: name, label: name })),
)

const forecastYear = computed(() => props.years[props.years.length - 1])

const columns = computed<PositionColumn[]>(() => [
  {
    key: 'label',
    label: t(`columns.${props.group}`),
    type: 'suggest',
    suggestions: suggestions.value,
    placeholder: t('namePlaceholder'),
  },
  ...props.years.map((year): PositionColumn => ({
    key: String(year),
    label: year === forecastYear.value ? t('forecast', { year }) : String(year),
    type: 'currency',
    fractionDigits: 0,
    total: true,
  })),
])

const accents: Record<IncomeGroupKey, string> = {
  active: 'bg-brand-blue-2',
  passive: 'bg-brand-teal',
  other: 'bg-ink-3',
}
</script>

<template>
  <section class="bg-surface-2! fh-card shadow-none!" @focusout="persistOnLeave">
    <header class="flex items-start gap-3 px-6 py-4">
      <span class="mt-1 h-8 w-1 flex-none rounded-full" :class="accents[group]" />
      <div>
        <h3 class="text-base font-bold">{{ t(`groups.${group}`) }}</h3>
        <p class="text-ink-3 text-sm">{{ t(`hints.${group}`) }}</p>
      </div>
    </header>

    <div class="py-5">
      <FinestCustomPositionListCard
        v-model:rows="rows"
        plain
        flush
        title=""
        :columns="columns"
        editable-rows
        @add-row="addDraft(blankIncomeRow(years))"
        @remove-row="removeRow" />
    </div>
  </section>
</template>

<i18n lang="json">
{
  "de": {
    "columns": {
      "active": "Aktives Einkommen",
      "passive": "Passives Einkommen",
      "other": "Sonstige Einkünfte"
    },
    "forecast": "{year} · Prognose",
    "groups": {
      "active": "Aktives Einkommen",
      "passive": "Passives Einkommen",
      "other": "Sonstige Einkünfte"
    },
    "hints": {
      "active": "Einkünfte aus deiner Arbeitskraft – jeweils als Jahresbetrag.",
      "passive": "Einkünfte aus deinem Vermögen – jeweils als Jahresbetrag.",
      "other": "Alles, was weder aus Arbeit noch aus Vermögen stammt – jeweils als Jahresbetrag."
    },
    "namePlaceholder": "Bezeichnung"
  },
  "en": {
    "columns": {
      "active": "Active income",
      "passive": "Passive income",
      "other": "Other income"
    },
    "forecast": "{year} · Forecast",
    "groups": {
      "active": "Active income",
      "passive": "Passive income",
      "other": "Other income"
    },
    "hints": {
      "active": "Income earned through work – as a yearly amount each.",
      "passive": "Income earned by your assets – as a yearly amount each.",
      "other": "Everything that comes neither from work nor from assets – as a yearly amount each."
    },
    "namePlaceholder": "Name"
  }
}
</i18n>
